import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogHeader, DialogBody, DialogFooter } from "@/components/ui/dialog";
import { AdminDialogHeading } from "@/components/admin/AdminDialogHeading";
import { cn } from "@/lib/utils";
import { throwIfNotOk } from "@/lib/api-error";
import { toast } from "@/lib/toast";
import { MediaPicker } from "@/components/ui/media-picker";
import {
  CircleNotch,
  DotsSixVertical,
  Plus,
  Trash,
  Pencil,
  ToggleLeft,
  ToggleRight,
  Image,
} from "@phosphor-icons/react";

import type { Specialization } from "@/types";
import { SPECIALIZATION_NAME_MAX, SPECIALIZATION_DESCRIPTION_MAX } from "@/schemas/specialization";

interface SpecFormDialogProps {
  title: string;
  name: string;
  description: string;
  iconUrl: string;
  onNameChange: (v: string) => void;
  onDescriptionChange: (v: string) => void;
  onIconUrlChange: (v: string) => void;
  onConfirm: () => void;
  onClose: () => void;
  confirmLabel: string;
  isPending: boolean;
}

function SpecFormDialog({
  title,
  name,
  description,
  iconUrl,
  onNameChange,
  onDescriptionChange,
  onIconUrlChange,
  onConfirm,
  onClose,
  confirmLabel,
  isPending,
}: SpecFormDialogProps) {
  return (
    <Dialog open onClose={onClose}>
      <DialogHeader onClose={onClose}>
        <AdminDialogHeading
          icon={Pencil}
          title={title}
          description="Uredite naziv, opis i ikonu specijalizacije."
        />
      </DialogHeader>
      <DialogBody>
        <div className="flex flex-col gap-4">
          <div className="space-y-2">
            <Label htmlFor="spec-name">Naziv *</Label>
            <Input
              id="spec-name"
              value={name}
              onChange={(e) => onNameChange(e.target.value)}
              maxLength={SPECIALIZATION_NAME_MAX}
              placeholder="npr. Kognitivno-bihevioralna terapija"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="spec-desc">Opis</Label>
            <Textarea
              id="spec-desc"
              value={description}
              onChange={(e) => onDescriptionChange(e.target.value)}
              maxLength={SPECIALIZATION_DESCRIPTION_MAX}
              placeholder="Kratki opis specijalizacije..."
              rows={3}
            />
          </div>
          <div className="space-y-2">
            <Label>Ikona</Label>
            <MediaPicker
              value={iconUrl}
              onChange={onIconUrlChange}
              folder="specialization-icons"
              accept="image/png,image/svg+xml,image/jpeg,image/webp"
              hint="PNG, SVG, JPG ili WebP"
              previewHeight="h-16"
            />
          </div>
        </div>
      </DialogBody>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Otkaži</Button>
        <Button onClick={onConfirm} disabled={!name.trim() || isPending}>
          {isPending && <CircleNotch className="size-4 animate-spin" />}
          {confirmLabel}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

export function SpecializationsSection() {
  const queryClient = useQueryClient();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingSpec, setEditingSpec] = useState<Specialization | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [draggedId, setDraggedId] = useState<string | null>(null);

  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formIconUrl, setFormIconUrl] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "specializations"],
    queryFn: async () => {
      const res = await fetch("/api/admin/specializations");
      await throwIfNotOk(res, "Greška pri učitavanju specijalizacija");
      return res.json() as Promise<{ data: Specialization[] }>;
    },
  });

  const specializations = data?.data ?? [];

  const createMutation = useMutation({
    mutationFn: async (body: { name: string; description?: string; iconUrl?: string }) => {
      const res = await fetch("/api/admin/specializations", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      await throwIfNotOk(res, "Greška pri kreiranju specijalizacije");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "specializations"] });
      toast.success("Specijalizacija kreirana");
      setShowAddDialog(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...body }: { id: string; name?: string; description?: string | null; iconUrl?: string | null; isActive?: boolean }) => {
      const res = await fetch(`/api/admin/specializations/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      await throwIfNotOk(res, "Greška pri ažuriranju specijalizacije");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "specializations"] });
      setEditingSpec(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/admin/specializations/${id}`, { method: "DELETE" });
      await throwIfNotOk(res, "Greška pri brisanju specijalizacije");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "specializations"] });
      toast.success("Specijalizacija obrisana");
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  const reorderMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const res = await fetch("/api/admin/specializations/reorder", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids }),
      });
      await throwIfNotOk(res, "Greška pri promjeni redoslijeda");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "specializations"] });
    },
  });

  const openAdd = () => {
    setFormName("");
    setFormDescription("");
    setFormIconUrl("");
    setShowAddDialog(true);
  };

  const openEdit = (spec: Specialization) => {
    setEditingSpec(spec);
    setFormName(spec.name);
    setFormDescription(spec.description || "");
    setFormIconUrl(spec.iconUrl || "");
  };

  const handleAdd = () => {
    createMutation.mutate({
      name: formName,
      description: formDescription || undefined,
      iconUrl: formIconUrl || undefined,
    });
  };

  const handleEdit = () => {
    if (!editingSpec) return;
    updateMutation.mutate({
      id: editingSpec.id,
      name: formName,
      // null clears it; undefined would leave the old description in place.
      description: formDescription.trim() || null,
      iconUrl: formIconUrl || null,
    });
  };

  const [localOrder, setLocalOrder] = useState<Specialization[] | null>(null);
  const displaySpecs = localOrder ?? specializations;

  const handleDragStart = (id: string) => {
    setDraggedId(id);
    setLocalOrder([...specializations]);
  };

  const handleDragOver = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    if (!draggedId || draggedId === targetId || !localOrder) return;
    const newOrder = [...localOrder];
    const fromIdx = newOrder.findIndex((s) => s.id === draggedId);
    const toIdx = newOrder.findIndex((s) => s.id === targetId);
    if (fromIdx === -1 || toIdx === -1) return;
    const [moved] = newOrder.splice(fromIdx, 1);
    newOrder.splice(toIdx, 0, moved);
    setLocalOrder(newOrder);
  };

  const handleDragEnd = () => {
    if (localOrder) {
      reorderMutation.mutate(localOrder.map((s) => s.id));
    }
    setDraggedId(null);
    setLocalOrder(null);
  };

  return (
    <>
      <div className="mt-10 border-t pt-8">
        <div className="flex items-center justify-between">
          <div>
            <h2
              className="text-xl font-bold tracking-tight text-foreground sm:text-2xl"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              Specijalizacije
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Definirajte specijalizacije koje terapeuti mogu odabrati prilikom prijave.
            </p>
          </div>
          <Button size="sm" onClick={openAdd}>
            <Plus className="size-4" />
            Dodaj
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="overflow-hidden rounded-xl border border-border/60 bg-card shadow-sm">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className={cn(
                "flex items-center gap-3 px-4 py-3",
                i !== 3 && "border-b border-border/40"
              )}
            >
              <div className="h-4 w-4 animate-pulse rounded bg-muted" />
              <div className="size-8 animate-pulse rounded-lg bg-muted" />
              <div className="space-y-1 flex-1">
                <div className="h-3.5 w-40 animate-pulse rounded bg-muted" />
                <div className="h-3 w-60 animate-pulse rounded bg-muted/50" />
              </div>
            </div>
          ))}
        </div>
      ) : displaySpecs.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border/60 bg-muted/10 px-6 py-16 text-center">
          <h3 className="text-base font-semibold text-foreground">
            Nema specijalizacija
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Dodajte prvu specijalizaciju klikom na dugme iznad.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-border/60 bg-card shadow-sm">
          {displaySpecs.map((spec, i) => (
            <div
              key={spec.id}
              draggable
              onDragStart={() => handleDragStart(spec.id)}
              onDragOver={(e) => handleDragOver(e, spec.id)}
              onDragEnd={handleDragEnd}
              className={cn(
                "flex items-center gap-3 px-4 py-3 transition-colors hover:bg-primary/[0.03]",
                i !== displaySpecs.length - 1 && "border-b border-border/40",
                draggedId === spec.id && "opacity-50",
                !spec.isActive && "opacity-40"
              )}
            >
              <DotsSixVertical className="size-4 shrink-0 cursor-grab text-muted-foreground/50 active:cursor-grabbing" />

              {spec.iconUrl ? (
                <img src={spec.iconUrl} alt="" className="size-8 shrink-0 rounded-lg object-cover" />
              ) : (
                <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted">
                  <Image className="size-3.5 text-muted-foreground/50" />
                </div>
              )}

              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-foreground">{spec.name}</div>
                {spec.description && (
                  <div className="truncate text-xs text-muted-foreground">{spec.description}</div>
                )}
              </div>

              <button
                onClick={() => updateMutation.mutate({ id: spec.id, isActive: !spec.isActive })}
                className="shrink-0 text-muted-foreground transition-colors hover:text-foreground"
                title={spec.isActive ? "Deaktiviraj" : "Aktiviraj"}
              >
                {spec.isActive ? (
                  <ToggleRight className="size-5 text-primary" />
                ) : (
                  <ToggleLeft className="size-5" />
                )}
              </button>

              <Button variant="ghost" size="icon-sm" onClick={() => openEdit(spec)}>
                <Pencil className="size-3.5" />
              </Button>

              <Button
                variant="ghost"
                size="icon-sm"
                className="text-muted-foreground hover:text-destructive"
                onClick={() => setDeleteConfirmId(spec.id)}
              >
                <Trash className="size-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Add Dialog */}
      {showAddDialog && (
        <SpecFormDialog
          title="Nova specijalizacija"
          name={formName}
          description={formDescription}
          iconUrl={formIconUrl}
          onNameChange={setFormName}
          onDescriptionChange={setFormDescription}
          onIconUrlChange={setFormIconUrl}
          onConfirm={handleAdd}
          onClose={() => setShowAddDialog(false)}
          confirmLabel="Kreiraj"
          isPending={createMutation.isPending}
        />
      )}

      {/* Edit Dialog */}
      {editingSpec && (
        <SpecFormDialog
          title="Uredi specijalizaciju"
          name={formName}
          description={formDescription}
          iconUrl={formIconUrl}
          onNameChange={setFormName}
          onDescriptionChange={setFormDescription}
          onIconUrlChange={setFormIconUrl}
          onConfirm={handleEdit}
          onClose={() => setEditingSpec(null)}
          confirmLabel="Sačuvaj"
          isPending={updateMutation.isPending}
        />
      )}

      {/* Delete Confirm */}
      {deleteConfirmId && (
        <Dialog open onClose={() => setDeleteConfirmId(null)}>
          <DialogHeader onClose={() => setDeleteConfirmId(null)}>
            <AdminDialogHeading
              icon={Trash}
              title="Brisanje specijalizacije"
              description="Ako je koriste terapeuti, brisanje neće biti moguće."
              tone="destructive"
            />
          </DialogHeader>
          <DialogBody className="flex flex-col gap-4">
            <p className="text-sm text-muted-foreground">
              Da li ste sigurni da želite obrisati ovu specijalizaciju? Deaktivirajte je umjesto toga ako je želite sakriti iz novih odabira.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>Otkaži</Button>
            <Button
              variant="destructive"
              onClick={() => {
                deleteMutation.mutate(deleteConfirmId);
                setDeleteConfirmId(null);
              }}
            >
              <Trash className="size-4" />
              Obriši
            </Button>
          </DialogFooter>
        </Dialog>
      )}
    </>
  );
}
